import {
  AUTOMATION_GRACE_PERIOD,
  AnyAutomation,
  AnyAutomationConfiguration,
  AutomationContext,
  AutomationCreator,
  AutomationParentScope,
  AutomationStatus,
  IAutomatedFormIssue,
  IAutomatedFormIssueConfiguration,
  IAutomatedNotificationConfiguration,
  IAutomationScopeData,
  IAutomationScopeRequirements,
  IGeneratedTaskConfiguration,
  IInvoice,
  ITask,
  ITemplateDefinition,
  PatientCollection,
  TemplateScope,
  TimingDirection,
  TreatmentPlanCollection,
  TreatmentStepCollection,
  isAutomatedFormIssueConfiguration,
  isAutomatedNotification,
  isAutomatedNotificationConfiguration,
  isGeneratedTask,
  isGeneratedTaskConfiguration,
  type IAppointment,
  type IAutomatedNotification,
  type IAutomation,
  type IAutomationResource,
  type IBrand,
  type IEvent,
  type IGeneratedTask,
  type IPatient,
  type ITreatmentPlan,
  type ITreatmentStep,
  type TreatmentStepAutomation,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  RequireProps,
  asDocRef,
  collectionGroupQuery,
  isSameRef,
  multiConcatMap,
  multiFilter,
  query$,
  sortTimestampAsc,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  where,
  type AtLeast,
  type INamedTypeDocument,
  type IReffable,
  type Timestamp,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { pick, sortBy } from 'lodash';
import * as moment from 'moment-timezone';
import { type Moment } from 'moment-timezone';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Appointment } from '../appointment/appointment';
import { TreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { AutomatedFormIssue } from '../custom-forms/automated-form-issue';
import { AutomatedNotification } from '../notification/automated-notification';
import { OrganisationCache } from '../organisation/organisation-cache';
import { GeneratedTask } from '../task/generated-task';
import { AutomationConfiguration } from './automation-configuration';
import { AutomationDisplay } from './automation-display';
import { AutomationTiming } from './automation-timing';

export class Automation {
  static init<Resource extends IAutomationResource>(
    overrides: AtLeast<
      IAutomation<Resource>,
      | 'type'
      | 'data'
      | 'creator'
      | 'triggerDate'
      | 'brandRef'
      | 'practiceRef'
      | 'parentScope'
    >
  ): IAutomation<Resource> {
    if (overrides.parentScope === AutomationParentScope.Patient) {
      if (!overrides.scopeData) {
        throw new Error('Parent scope requires scope data');
      }
      return {
        status: AutomationStatus.Pending,
        ...overrides,
        parentScope: AutomationParentScope.Patient,
        scopeData: overrides.scopeData,
        creator: pick(overrides.creator, ['name', 'ref', 'type']),
        cloudTaskHistory: [],
      };
    }
    return {
      status: AutomationStatus.Pending,
      ...overrides,
      parentScope: AutomationParentScope.TreatmentStep,
      creator: pick(overrides.creator, ['name', 'ref', 'type']),
      cloudTaskHistory: [],
    };
  }

  static automations$(
    brand: IReffable<IBrand>,
    dateFrom: Timestamp,
    dateTo: Timestamp
  ): Observable<WithRef<IAutomation<AnyAutomation>>[]> {
    const stepAutomations$ = query$(
      collectionGroupQuery<IAutomation<AnyAutomation>>(
        TreatmentStepCollection.Automations
      ),
      where('triggerDate', '>=', dateFrom),
      where('triggerDate', '<=', dateTo),
      where('brandRef', '==', brand.ref)
    );

    const patientAutomations$ = query$(
      collectionGroupQuery<IAutomation<AnyAutomation>>(
        PatientCollection.Automations
      ),
      where('triggerDate', '>=', dateFrom),
      where('triggerDate', '<=', dateTo),
      where('brandRef', '==', brand.ref)
    );

    return combineLatest([stepAutomations$, patientAutomations$]).pipe(
      map(([stepAutomations, patientAutomations]) => [
        ...stepAutomations,
        ...patientAutomations,
      ])
    );
  }

  static getAutomations$(
    brand: WithRef<IBrand>,
    event: IEvent,
    triggerAfterDate?: Timestamp
  ): Observable<IAutomation<AnyAutomation>[]> {
    return combineLatest([
      Automation.getNotificationAutomations$(brand, event, triggerAfterDate),
      Automation.getGeneratedTaskAutomations$(brand, event, triggerAfterDate),
      Automation.getAutomatedFormIssueAutomations$(
        brand,
        event,
        triggerAfterDate
      ),
    ]).pipe(
      map(([notifications, tasks, forms]) => [
        ...notifications,
        ...tasks,
        ...forms,
      ])
    );
  }

  static async generateForAppointment(
    config: WithRef<AnyAutomationConfiguration>,
    appointment: WithRef<RequireProps<IAppointment, 'event'>>,
    treatmentStep: WithRef<ITreatmentStep>,
    scopeData: IAutomationScopeRequirements
  ): Promise<IAutomation<AnyAutomation> | undefined> {
    if (!AutomationConfiguration.isActive(config)) {
      return;
    }
    const brand = await OrganisationCache.brands.getDoc(
      Firestore.getParentDocRef<IBrand>(config.ref)
    );
    const practice = config.practiceRef
      ? await OrganisationCache.practices.getDoc(config.practiceRef)
      : undefined;

    if (
      !AutomationConfiguration.isForTreatmentStep(config, treatmentStep) ||
      !AutomationConfiguration.isForPractice(config, appointment.practice.ref)
    ) {
      return;
    }

    const matchingTreatmentConfigRef = treatmentStep.treatments
      .map((treatment) => treatment.config.ref)
      .find((treatmentConfigRef) =>
        config.treatmentRefs.some((treatmentRef) =>
          isSameRef(treatmentConfigRef, treatmentRef)
        )
      );

    const treatmentConfig = matchingTreatmentConfigRef
      ? await Firestore.getDoc(matchingTreatmentConfigRef)
      : undefined;

    const creator: INamedTypeDocument<object, AutomationCreator> =
      config.treatmentRefs.length && treatmentConfig
        ? {
            ...toNamedDocument(treatmentConfig),
            type: AutomationCreator.TreatmentConfiguration,
          }
        : practice
          ? {
              ...toNamedDocument(practice),
              type: AutomationCreator.Practice,
            }
          : {
              ...toNamedDocument(brand),
              type: AutomationCreator.Brand,
            };

    const triggerAfterDate = Appointment.getCompletedAt(appointment);

    if (isAutomatedNotificationConfiguration(config)) {
      return AutomatedNotification.generateFromConfig(
        config as WithRef<IAutomatedNotificationConfiguration>,
        creator,
        appointment.event,
        brand.ref,
        scopeData,
        triggerAfterDate
      );
    }

    if (isGeneratedTaskConfiguration(config)) {
      return GeneratedTask.generateFromConfig(
        config as WithRef<IGeneratedTaskConfiguration>,
        creator,
        appointment.event,
        brand.ref,
        scopeData,
        triggerAfterDate
      );
    }

    if (isAutomatedFormIssueConfiguration(config)) {
      return AutomatedFormIssue.generateFromConfig(
        config as WithRef<IAutomatedFormIssueConfiguration>,
        creator,
        appointment.event,
        brand.ref,
        triggerAfterDate
      );
    }
  }

  static async fromTask(
    task: ITask
  ): Promise<WithRef<IAutomation<IAutomationResource>> | undefined> {
    if (!task.automationSourceRef) {
      return;
    }

    return Firestore.getDoc(task.automationSourceRef);
  }

  static getNotificationAutomations$(
    brand: WithRef<IBrand>,
    event: IEvent,
    triggerAfterDate?: Timestamp
  ): Observable<IAutomation<IAutomatedNotification>[]> {
    return AutomationConfiguration.getNotificationConfigurations$(brand).pipe(
      multiFilter(
        (config) =>
          config.isActive &&
          AutomationConfiguration.isGlobal(config) &&
          AutomationConfiguration.isForPractice(config, event.practice.ref)
      ),
      multiConcatMap((notification) => {
        let creator: INamedTypeDocument<object, AutomationCreator> = {
          ...toNamedDocument(brand),
          type: AutomationCreator.Brand,
        };

        if (notification.practiceRef) {
          creator = {
            ...event.practice,
            type: AutomationCreator.Practice,
          };
        }

        return AutomatedNotification.generateFromConfig(
          notification,
          creator,
          event,
          brand.ref,
          { parentScope: AutomationParentScope.TreatmentStep },
          triggerAfterDate
        );
      })
    );
  }

  static getGeneratedTaskAutomations$(
    brand: WithRef<IBrand>,
    event: IEvent,
    triggerAfterDate?: Timestamp
  ): Observable<IAutomation<IGeneratedTask>[]> {
    return AutomationConfiguration.getGeneratedTaskConfigurations$(brand).pipe(
      multiFilter(
        (config) =>
          config.isActive &&
          AutomationConfiguration.isGlobal(config) &&
          AutomationConfiguration.isForPractice(config, event.practice.ref)
      ),
      multiConcatMap((task) => {
        let creator: INamedTypeDocument<object, AutomationCreator> = {
          ...toNamedDocument(brand),
          type: AutomationCreator.Brand,
        };

        if (task.practiceRef) {
          creator = {
            ...event.practice,
            type: AutomationCreator.Practice,
          };
        }

        return GeneratedTask.generateFromConfig(
          task,
          creator,
          event,
          brand.ref,
          { parentScope: AutomationParentScope.TreatmentStep },
          triggerAfterDate
        );
      })
    );
  }

  static determineScope<T extends IAutomationResource>(
    automation: IAutomation<T>,
    template?: WithRef<ITemplateDefinition>
  ): TemplateScope {
    if (automation.parentScope === AutomationParentScope.Patient) {
      return automation.scopeData.scope;
    }

    if (isGeneratedTask(automation.data) && automation.data.scope) {
      return automation.data.scope;
    }

    if (
      isAutomatedNotification(automation.data) &&
      automation.data.customTemplate?.scope
    ) {
      return automation.data.customTemplate.scope;
    }

    if (template?.scope) {
      return template.scope;
    }

    return TemplateScope.Appointment;
  }

  static buildScopeData(
    scope: TemplateScope,
    documentRef: DocumentReference<unknown>
  ): IAutomationScopeData {
    switch (scope) {
      case TemplateScope.Patient:
        return {
          scope,
          patientRef: documentRef as DocumentReference<IPatient>,
        };
      case TemplateScope.Appointment:
        return {
          scope,
          appointmentRef: documentRef as DocumentReference<IAppointment>,
        };
      case TemplateScope.TreatmentPlan:
        return {
          scope,
          treatmentPlanRef: documentRef as DocumentReference<ITreatmentPlan>,
        };
      case TemplateScope.TreatmentStep:
        return {
          scope,
          treatmentStepRef: documentRef as DocumentReference<ITreatmentStep>,
        };
      case TemplateScope.Invoice:
        return {
          scope,
          invoiceRef: documentRef as DocumentReference<IInvoice>,
        };
      case TemplateScope.None:
        return { scope: TemplateScope.None };
      default:
        throw new Error(`Unsupported scope`);
    }
  }

  static resolveContext(
    automation: WithRef<IAutomation<IAutomationResource>>
  ): AutomationContext {
    const isStepAutomation = automation.ref.path.includes(
      TreatmentPlanCollection.TreatmentSteps
    );
    return isStepAutomation
      ? AutomationContext.Appointment
      : AutomationContext.Marketing;
  }

  static getAutomatedFormIssueAutomations$(
    brand: WithRef<IBrand>,
    event: IEvent,
    triggerAfterDate?: Timestamp
  ): Observable<IAutomation<IAutomatedFormIssue>[]> {
    return AutomationConfiguration.getAutomatedFormIssueConfigurations$(
      brand
    ).pipe(
      multiFilter(
        (config) =>
          config.isActive &&
          AutomationConfiguration.isGlobal(config) &&
          AutomationConfiguration.isForPractice(config, event.practice.ref)
      ),
      multiConcatMap((task) => {
        let creator: INamedTypeDocument<object, AutomationCreator> = {
          ...toNamedDocument(brand),
          type: AutomationCreator.Brand,
        };

        if (task.practiceRef) {
          creator = {
            ...event.practice,
            type: AutomationCreator.Practice,
          };
        }

        return AutomatedFormIssue.generateFromConfig(
          task,
          creator,
          event,
          brand.ref,
          triggerAfterDate
        );
      })
    );
  }

  static patientRef<Resource extends IAutomationResource>(
    automation: WithRef<IAutomation<Resource>>
  ): DocumentReference<IPatient> {
    if (automation.parentScope !== AutomationParentScope.Patient) {
      const treatmentStepRef = Firestore.getParentDocRef<ITreatmentStep>(
        automation.ref
      );
      const treatmentPlanRef =
        Firestore.getParentDocRef<ITreatmentPlan>(treatmentStepRef);
      return Firestore.getParentDocRef<IPatient>(treatmentPlanRef);
    }

    return Firestore.getParentDocRef<IPatient>(automation.ref);
  }

  static async resolveAppointment<Resource extends IAutomationResource>(
    automation: WithRef<IAutomation<Resource>>
  ): Promise<WithRef<IAppointment> | undefined> {
    if (automation.parentScope !== AutomationParentScope.Patient) {
      const treatmentStepRef = asDocRef<ITreatmentStep>(
        Firestore.getParentDocRef(automation.ref)
      );
      const treatmentStep = await Firestore.getDoc(treatmentStepRef);
      return TreatmentStep.appointment(treatmentStep);
    }

    if (automation.scopeData.scope === TemplateScope.Appointment) {
      return Firestore.getDoc(automation.scopeData.appointmentRef);
    }

    if (automation.scopeData.scope === TemplateScope.TreatmentStep) {
      const treatmentStep = await Firestore.getDoc(
        automation.scopeData.treatmentStepRef
      );
      return TreatmentStep.appointment(treatmentStep);
    }
  }

  static async appointmentRef(
    automation: WithRef<IAutomation<IAutomationResource>>
  ): Promise<DocumentReference<IAppointment> | undefined> {
    if (automation.parentScope !== AutomationParentScope.Patient) {
      const treatmentStepRef = asDocRef<ITreatmentStep>(
        Firestore.getParentDocRef(automation.ref)
      );
      const treatmentStep = await Firestore.getDoc(treatmentStepRef);
      return treatmentStep.appointment;
    }

    if (automation.scopeData.scope === TemplateScope.Appointment) {
      return automation.scopeData.appointmentRef;
    }

    if (automation.scopeData.scope === TemplateScope.TreatmentStep) {
      const treatmentStep = await Firestore.getDoc(
        automation.scopeData.treatmentStepRef
      );
      return treatmentStep.appointment;
    }
  }

  static async treatmentStepRef(
    automation: WithRef<IAutomation<IAutomationResource>>
  ): Promise<DocumentReference<ITreatmentStep> | undefined> {
    if (automation.parentScope !== AutomationParentScope.Patient) {
      return asDocRef<ITreatmentStep>(
        Firestore.getParentDocRef(automation.ref)
      );
    }

    if (automation.scopeData.scope === TemplateScope.Appointment) {
      const appointment = await Firestore.getDoc(
        automation.scopeData.appointmentRef
      );
      return appointment.treatmentPlan.treatmentStep.ref;
    }

    if (automation.scopeData.scope === TemplateScope.TreatmentStep) {
      return automation.scopeData.treatmentStepRef;
    }
  }

  static async treatmentPlanRef(
    automation: WithRef<IAutomation<IAutomationResource>>
  ): Promise<DocumentReference<ITreatmentPlan> | undefined> {
    if (
      automation.parentScope === AutomationParentScope.Patient &&
      automation.scopeData.scope === TemplateScope.TreatmentPlan
    ) {
      return automation.scopeData.treatmentPlanRef;
    }

    const treatmentStepRef = await Automation.treatmentStepRef(automation);
    if (!treatmentStepRef) {
      return;
    }

    return Firestore.getParentDocRef<ITreatmentPlan>(treatmentStepRef);
  }

  static isGoingToRun<T extends IAutomationResource>(
    automation: AtLeast<IAutomation<T>, 'status'>
  ): boolean {
    const willNotRun = [AutomationStatus.Disabled, AutomationStatus.Cancelled];
    if (willNotRun.includes(automation.status)) {
      return false;
    }
    if (Automation.hasAlreadyRun(automation)) {
      return false;
    }
    return true;
  }

  static hasAlreadyRun<T extends IAutomationResource>(
    automation: AtLeast<IAutomation<T>, 'status'>
  ): boolean {
    const hasAlreadyRun = [
      AutomationStatus.Completed,
      AutomationStatus.Failed,
      AutomationStatus.Skipped,
    ];
    return hasAlreadyRun.includes(automation.status);
  }

  static isStale<T extends IAutomationResource>(
    automation: WithRef<IAutomation<T>>,
    timezone: Timezone
  ): boolean {
    if (!automation.triggerDate || !automation.cloudTask) {
      return false;
    }

    const isPendingOrQueued =
      automation.status === AutomationStatus.Pending ||
      automation.status === AutomationStatus.Queued;

    if (!isPendingOrQueued) {
      return false;
    }

    const startOfToday = toMomentTz(moment(), timezone).startOf('day');
    return toMoment(automation.triggerDate).isBefore(startOfToday);
  }

  static isReadyForQueue<T extends IAutomationResource>(
    automation: WithRef<IAutomation<T>>
  ): boolean {
    if (automation.data.timing.direction === TimingDirection.After) {
      const hasTriggerAfterDate = !!automation.triggerAfterDate;
      return hasTriggerAfterDate;
    }
    return true;
  }

  static shouldUpsertCloudTask(
    automation: WithRef<IAutomation<TreatmentStepAutomation>>
  ): boolean {
    return !this.hasAlreadyRun(automation) && this.isReadyForQueue(automation);
  }

  static getExpectedTriggerDate(
    automation: IAutomation<IAutomationResource>,
    event: IEvent,
    timezone: Timezone
  ): Moment {
    return toMomentTz(
      AutomationTiming.getTriggerDateFromEvent(
        event,
        automation.data.timing,
        timezone
      ),
      timezone
    );
  }

  static applyGracePeriod(
    expectedTriggerDate: Moment,
    timezone: Timezone,
    currentDateTime: Moment = moment()
  ): Moment {
    const now = currentDateTime.tz(timezone);
    const runImmediately = now.clone().add(AUTOMATION_GRACE_PERIOD);
    return expectedTriggerDate.isSameOrBefore(runImmediately)
      ? runImmediately
      : expectedTriggerDate;
  }

  static async setTriggerAfterDate(
    automation: WithRef<IAutomation<IAutomationResource>>,
    completedAt?: Timestamp
  ): Promise<void> {
    const resetToPending = Automation.isGoingToRun(automation) && !completedAt;
    await Firestore.saveDoc({
      ...automation,
      status: resetToPending ? AutomationStatus.Pending : automation.status,
      triggerAfterDate: completedAt,
    });
  }

  static sort<T extends IAutomation<AnyAutomation>>(automations: T[]): T[] {
    const byLabel = sortBy(automations, (automation) =>
      AutomationDisplay.getLabel(automation.data)
    );
    const byTriggerDate = byLabel.sort((a, b) =>
      sortTimestampAsc(a.triggerDate, b.triggerDate)
    );
    return byTriggerDate;
  }

  static adjustStatusAndTriggerDate<T extends IAutomation<AnyAutomation>>(
    automation: T,
    event: IEvent,
    timezone: Timezone,
    customNow?: moment.Moment
  ): T {
    const now = customNow ? customNow : toMomentTz(moment(), timezone);
    const expectedTriggerDate = Automation.getExpectedTriggerDate(
      automation,
      event,
      timezone
    );

    if (expectedTriggerDate.isBefore(now, 'day')) {
      automation.status = AutomationStatus.Cancelled;
      automation.triggerDate = toTimestamp(expectedTriggerDate);
      return automation;
    }

    const triggerDate = Automation.applyGracePeriod(
      expectedTriggerDate,
      timezone,
      now
    );
    automation.triggerDate = toTimestamp(triggerDate);
    return automation;
  }
}
